import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { ErrorNote } from "@/components/ErrorNote";
import { products, type RefSlotRow } from "@/api/client";
import { fmtBytes } from "@/lib/format";
import { REF_TYPE_DESC, REF_TYPE_LABEL, qk, useProductRefFiles } from "@/modules/products/data";
import { ChoosePrompt, ModelSelect, ProductSelect, ScopeBar } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

import { Icon } from "@/components/icons";
/** Reference files — the DID maps and signal sets a bench reads.
 *
 *  THE JOURNEY, in Dattatray's words: *"I should be first able to select
 *  Product -> then either common ref file or for a specific Model -> Then
 *  click upload. Its a journey."* And: *"Again No Pop up just breaks the
 *  UX."*
 *
 *  So the scope bar IS the journey, left to right, and the upload happens
 *  INLINE under the slot it belongs to. A modal was the wrong shape twice
 *  over: it hid the thing being changed behind the form changing it, and it
 *  had to re-ask for the product and model the page already knew.
 *
 *  D-90: a reference file is a DESCRIPTION, so it inherits — common to the
 *  product unless a model has its own, and a company may override either,
 *  per ref_type independently. The four levels resolve most-specific-first;
 *  this page shows one scope at a time so nobody has to guess which file a
 *  bench would actually get.
 *
 *  EVERY UPLOAD IS A NEW VERSION (never an overwrite), which is why the
 *  history is worth showing: it IS the rollback path. Dattatray asked for it
 *  directly — *"where do I see all the reference file per product as well as
 *  per model archived file everything."*
 */
export function ReferenceFilesPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [modelId, setModelId] = useState("");
  // ALWAYS INCLUDE THE ARCHIVED ONES. There used to be a "Show: current only
  // / include superseded" selector defaulting to current-only, and it hid the
  // one thing the page exists for. Dattatray, after archiving every version
  // of a slot: *"actually I am now not able to see archived files at all."*
  // The slot read Empty, the ten archived versions were unreachable, and the
  // way back was a selector in the scope bar that nothing pointed at.
  //
  // Uploads never overwrite (D-90), so the ARCHIVE IS THE ROLLBACK PATH — a
  // bad DID map is undone by pointing at an earlier file again. A filter that
  // hides it by default is a filter that hides the recovery, and the state was
  // invisible: a slot with every version archived looks identical to a slot
  // nobody ever uploaded to.
  const q = useProductRefFiles(kind, modelId || undefined, true);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Bench reference files"
        desc="What a bench reads to know the ECU: application DIDs, manufacturing DIDs and the signal set. Common to a product, or a model's own."
      />

      <ScopeBar>
        {/* SW-enabled products only (D-95) - a DID map describes software talking to an ECU. */}
        <ProductSelect onlyWithSoftware value={kind} onChange={(k) => { setKind(k); setModelId(""); }} />
        <ModelSelect kind={kind} value={modelId} onChange={setModelId} />
      </ScopeBar>

      {kind && <div className="section-label">Reference files</div>}
      {!kind && <ChoosePrompt what="reference files" />}
      {kind && q.isLoading && <div className="panel panel-pad cap">Loading…</div>}

      {kind && !q.isLoading && (
        <div className="panel panel-pad">
          {/* THE COUNT OF WHAT IS MISSING, on the header - Fabric's
              "3 slots · N missing". It is the one number worth reading before
              the list: a slot with nothing in it is a bench that downloads
              nothing. */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10,
            paddingBottom: 8 }}>
            <span style={{ fontWeight: 700 }}>
              {(q.data?.slots ?? []).length} slot{(q.data?.slots ?? []).length === 1 ? "" : "s"}
            </span>
            {(() => {
              const missing = (q.data?.slots ?? []).filter((s2) => !s2.current).length;
              return missing
                ? <Chip tone="warn">{missing} empty</Chip>
                : <Chip tone="ok">all filled</Chip>;
            })()}
            <span className="cap" style={{ color: "var(--t-dim)" }}>
              {modelId
                ? "this model's own files - a slot left empty falls back to the product's common file"
                : "the product's common files - every model inherits these unless it has its own"}
            </span>
          </div>
          {(q.data?.slots ?? []).map((slot) => (
            <SlotRow key={slot.ref_type} kind={kind} modelId={modelId}
              slot={slot} mayAuthor={mayAuthor} />
          ))}
        </div>
      )}
    </div>
  );
}


/** ONE ROW PER SLOT, Fabric's shape. Dattatray: *"for reference files the
 *  Fabric UI was so much better."*
 *
 *  It was, and the difference is density and constancy. Fabric renders each
 *  slot as a single line —
 *
 *      Application config DID    app_config.json    [upload] [download] [archive]
 *
 *  — so all three slots and what is missing from them fit in one glance. The
 *  icons are ALWAYS there and merely dimmed when there is nothing to act on,
 *  so the row never changes shape and your eye learns where the buttons are.
 *
 *  ORBIT HAD THREE TALL PANELS, each with a heading, a description line, a
 *  chip, a details block, and an "Upload new version" button that revealed a
 *  form you then filled in. Two clicks and a scroll to replace a file, and
 *  you could not see the other slots while doing it.
 *
 *  ONE CLICK TO UPLOAD, which is the real win: the upload icon is a `<label>`
 *  over a hidden file input, and picking a file submits — Fabric's
 *  `data-auto-submit-form`. There is no form to reveal because the scope bar
 *  already answered every question it would have asked, which is exactly what
 *  Dattatray meant by *"it's a journey"*.
 *
 *  WHAT IS ORBIT'S AND NOT FABRIC'S: archiving is reversible here and delete
 *  refuses a live file, because every upload is a NEW VERSION (D-90) and the
 *  history is the rollback path. So the archived block offers revive as well
 *  as delete-forever, and the live row offers archive but never delete.
 */
function SlotRow({ kind, modelId, slot, mayAuthor }: {
  kind: string; modelId: string; slot: RefSlotRow; mayAuthor: boolean;
}) {
  const qc = useQueryClient();
  const [showOld, setShowOld] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["products", kind, "ref-files"] });
    void qc.invalidateQueries({ queryKey: qk.products });
  };

  const upload = useMutation({
    mutationFn: (f: File) => products.uploadRefFile(kind, f, slot.ref_type,
      { model_id: modelId || undefined }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (v: { id: string; on: boolean }) =>
      products.setRefFileArchived(kind, v.id, v.on),
    onSuccess: refresh,
  });
  const drop = useMutation({
    mutationFn: (id: string) => products.deleteRefFile(kind, id),
    onSuccess: refresh,
  });

  const cur = slot.current;
  const busy = upload.isPending || archive.isPending || drop.isPending;
  const old = slot.history;
  const label = REF_TYPE_LABEL[slot.ref_type] ?? slot.ref_type;

  return (
    <div style={{ borderTop: "1px solid var(--line)", padding: "10px 0" }}>
      <div style={{ display: "grid",
        gridTemplateColumns: "minmax(190px,1.1fr) minmax(0,2fr) auto",
        gap: 12, alignItems: "center" }}>

        <div style={{ minWidth: 0 }} title={REF_TYPE_DESC[slot.ref_type] ?? ""}>
          <div style={{ fontWeight: 700, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
          <div className="mono cap">{slot.ref_type}</div>
        </div>

        <div style={{ minWidth: 0, display: "flex", alignItems: "baseline", gap: 8 }}>
          {cur ? (
            <>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis",
                whiteSpace: "nowrap" }}
                title={cur.filename}>
                {cur.filename}
              </span>
              <span className="cap" style={{ whiteSpace: "nowrap" }}>
                {fmtBytes(cur.size_bytes)}
              </span>
            </>
          ) : (
            // "Empty" IS the answer, said plainly. A blank cell reads as
            // "still loading" and a missing row reads as "not applicable".
            <span className="cap" style={{ color: "var(--t-dim)" }}>
              {old.length
                // A SLOT WITH EVERY VERSION ARCHIVED is not the same state as
                // one nobody ever uploaded to, and it has a way out. Saying
                // "Empty" alone was what made the archive look lost.
                ? `Empty - every version is archived; restore one below to serve it again`
                : `Empty - ${modelId
                    ? "this model has none; a bench falls back to the common file"
                    : "nothing uploaded for this product"}`}
            </span>
          )}
        </div>

        {/* THE ICONS ARE ALWAYS PRESENT, dimmed when there is nothing to act
            on, so the row never changes shape between slots. */}
        <span style={{ display: "flex", gap: 6 }}>
          {mayAuthor && (
            <label className="btn ghost icon" title="Upload a new version"
              style={{ cursor: busy ? "default" : "pointer" }}>
              <Icon name="upload" />
              <input type="file" hidden disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  // PICKING IS THE SUBMIT (Fabric's auto-submit). The value is
                  // cleared so choosing the same file twice fires again —
                  // otherwise a failed upload cannot be retried.
                  e.target.value = "";
                  if (f) upload.mutate(f);
                }} />
            </label>
          )}
          <a className="btn ghost icon"
            style={{ pointerEvents: cur ? undefined : "none",
              opacity: cur ? 1 : 0.4 }}
            href={cur ? products.refFileHref(kind, cur.file_id) : undefined}
            title={cur ? "Download" : "Nothing to download"}>
            <Icon name="download" />
          </a>
          {mayAuthor && (
            <button className="btn red icon" disabled={!cur || busy}
              title={cur
                ? "Archive - the previous version becomes the one a bench gets; revivable"
                : "Nothing to archive"}
              onClick={() => cur && archive.mutate({ id: cur.file_id, on: true })}>
              <Icon name="archive" />
            </button>
          )}
        </span>
      </div>

      {slot.overrides.length > 0 && (
        <div style={{ marginTop: 6, marginLeft: 8 }}>
          {slot.overrides.map((o) => (
            <div key={o.file_id} className="cap" style={{ display: "flex", gap: 8 }}>
              <Chip tone="warn">override</Chip>
              <span className="mono">{o.company_id}</span>
              <span>{o.filename}</span>
            </div>
          ))}
        </div>
      )}

      <ErrorNote error={upload.error ?? archive.error ?? drop.error} />

      {old.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <button onClick={() => setShowOld((v) => !v)}
            style={{ background: "none", border: "none", cursor: "pointer",
              color: "var(--teal)", fontSize: 12, fontWeight: 700, padding: 0 }}>
            <Icon name={showOld ? "collapse" : "expand"} />{" "}
            {old.length} earlier version{old.length === 1 ? "" : "s"}
          </button>
          {showOld && (
            <div style={{ marginLeft: 14, paddingLeft: 12,
              borderLeft: "2px solid var(--line2)", marginTop: 4 }}>
              {old.map((h) => (
                <div key={h.file_id} style={{ display: "flex", gap: 8,
                  alignItems: "center", padding: "3px 0" }}>
                  {/* NO VERSION NUMBER. Dattatray: *"why do I need version info
                      for archived files? Only file name is enough."* The
                      filenames already carry it - `appConfRef_v9.xlsx` - and
                      the row's own `version` is a separate counter that does
                      NOT agree with it, so the line read "v8 appConfRef_v9"
                      and invited the reader to reconcile two numbers that
                      answer different questions. The name is the one people
                      recognise, so it is the one shown. */}
                  <span style={{ minWidth: 0, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {h.filename}
                  </span>
                  {/* NO "superseded" TAG. Dattatray: *"superseded tag is not
                      required on archived reference files."* The block is
                      already titled "N earlier versions", so every row in it
                      is earlier by construction - the chip restated the
                      heading on every line. "archived" stays, because it is
                      not a restatement: it is the state that decides whether
                      this row can be deleted forever. */}
                  {h.archived && <Chip tone="mute">archived</Chip>}
                  <a className="btn ghost icon"
                    href={products.refFileHref(kind, h.file_id)} title="Download">
                    <Icon name="download" />
                  </a>
                  {/* RESTORE ON EVERY EARLIER VERSION, not only archived
                      ones. Dattatray: *"can not see how I can restore an
                      archived version back to working version!"* - and the
                      screenshot showed why: those rows were SUPERSEDED but
                      not archived, so they got an archive button and no way
                      back. The only route to promoting an older file was to
                      archive each newer one in turn, which is a stack to
                      unwind rather than an action.

                      Restore is slot-replace (it archives whatever is live),
                      so it means the same thing for both states: make THIS
                      the file a bench gets. */}
                  {mayAuthor && (
                    <button className="btn teal icon" disabled={busy}
                      title="Make this the version a bench downloads"
                      onClick={() => archive.mutate({ id: h.file_id, on: false })}>
                      <Icon name="restore" />
                    </button>
                  )}
                  {mayAuthor && !h.archived && (
                    <button className="btn red icon" disabled={busy}
                      title="Archive this version"
                      onClick={() => archive.mutate({ id: h.file_id, on: true })}>
                      <Icon name="archive" />
                    </button>
                  )}
                  {/* DELETE ONLY ONCE ARCHIVED - the safeguard rather than a
                      step: a bench resolves to the newest LIVE file, so
                      archiving first makes the change visible and reversible
                      before anything is destroyed. */}
                  {mayAuthor && h.archived && (
                    <button className="btn red icon" disabled={busy}
                      title="Delete forever - bytes included"
                      onClick={() => drop.mutate(h.file_id)}>
                      <Icon name="delete" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
